import { Add, ArrowBack, Close } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { useAccess } from '../../../hooks/useAccess';
import {
  addTestCasesToSuite,
  getTestCasesBySuite,
  removeTestCasesfromSuite,
} from '../../../modules/testsuites';
import { getConfigurations } from '../../../modules/config';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import Modal from '../../../ui/layout/Modal';
import TableComponent from '../../../ui/data/table/TableComponent';
import { tableHeader } from '../test-case/testCaseTableHead';
import AddTestCaseInSuite from './AddTestCaseInSuite';
import ActionButton from '../../../ui/primitives/ActionButton';

const ViewTestCaseModal = ({
  show,
  handleClose,
  selectedSuite,
  fetchTestSuitesData,
}) => {
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState('view');
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [removeTestCase, setRemoveTestCase] = useState(null);
  const [technologyLayerOptions, setTechnologyLayerOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const isSuiteSuspended = selectedSuite?.status === 'suspended';

  const { canAccess } = useAccess();
  const canManage = canAccess('ManageTestSuite');

  const fetchTestCases = async () => {
    if (!selectedSuite?.id) return;

    setLoading(true);
    try {
      const response = await getTestCasesBySuite(selectedSuite?.id, page, perPage);
      setData(response.testcases);
      setTotalCount(response.total);
      setError(null);
    } catch (error) {
      setError(error.message || 'Unable to fetch test cases');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const fetchConfig = async () => {
    try {
      const [technologyLayerResponse] = await Promise.all([
        getConfigurations('technologylayer', projectId),
      ]);
      if (technologyLayerResponse) {
        setTechnologyLayerOptions(technologyLayerResponse.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (show) {
      fetchTestCases();
      fetchConfig();
    }
  }, [show, page, perPage, order]);

  const handleCloseButton = () => {
    setSelected([]);
    setMode('view');
    setPage(1);
    setTotalCount(0);
    handleClose();
    setData([]);
  };

  const handleToggleModes = (mode) => {
    setMode(mode);
  };

  const handleAddTestCases = async () => {
    setLoading(true);
    const testCaseIds = selected;
    try {
      await addTestCasesToSuite(selectedSuite?.id, testCaseIds);
      addNotification('success', 'Test cases added successfully.');
      fetchTestSuitesData();
      handleCloseButton();
    } catch (error) {
      const errorMessage = error?.message || 'Failed to fetch test cases.';
      addNotification('danger', errorMessage);
    }
    setLoading(false);
  };

  const handleTestCaseRemove = async () => {
    try {
      await removeTestCasesfromSuite(selectedSuite?.id, removeTestCase?.id);
      fetchTestCases();
      fetchTestSuitesData();
      addNotification('success', 'Test case has been removed from test suite.');
    } catch (error) {
      const errorMessage = error?.message || 'Failed to remove test case.';
      addNotification('danger', errorMessage);
    }
    setShowConfirmationModal(false);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  const modalTitle = (
    <Box component="span">
      Manage Test Cases -{' '}
      <Typography
        component="span"
        fontWeight={700}
        color="primary.main"
        variant="inherit">
        {selectedSuite?.name || 'Unnamed Suite'}
        {isSuiteSuspended && (
          <Typography
            component="span"
            variant="caption"
            color="error.main"
            sx={{ ml: 0.75 }}>
            (Suspended)
          </Typography>
        )}
      </Typography>
    </Box>
  );

  return (
    <>
      <Modal
        modalTitle={modalTitle}
        open={show}
        handleClose={handleCloseButton}
        width="lg"
        action={() => (
          <>
            <ActionButton
              intent="secondary"
              label="Close"
              onClick={handleCloseButton}
            />
            {mode === 'add' && (
              <ActionButton
                intent="primary"
                label="Add"
                disabled={selected.length === 0}
                onClick={handleAddTestCases}
              />
            )}
          </>
        )}>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Typography color="error.main" textAlign="center" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {mode === 'view' ? (
            <>
              {canManage && !isSuiteSuspended && (
                <Box sx={{ mb: 2 }}>
                  <ActionButton
                    intent="primary"
                    icon={<Add />}
                    label="Add Test Cases"
                    onClick={() => handleToggleModes('add')}
                  />
                </Box>
              )}
              <TableComponent
                data={data}
                headerTabs={tableHeader}
                order={order}
                setOrder={setOrder}
                defaultSort="created_at"
                isLoading={loading}
                striped
                page={page}
                perPage={perPage}
                totalCount={totalCount}
                showPages={true}
                showCount={true}
                handlePageChange={handlePageChange}
                setPerPage={handlePageSizeChange}
                showRowCount={true}
                action={(row) =>
                  canManage &&
                  !isSuiteSuspended && (
                    <IconButton
                      onClick={() => {
                        setShowConfirmationModal(true);
                        setRemoveTestCase(row);
                      }}
                      title="Remove Test Case">
                      <Close fontSize="small" sx={{ color: 'error.main' }} />
                    </IconButton>
                  )
                }
              />
            </>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <ActionButton
                  intent="primary"
                  icon={<ArrowBack fontSize="small" />}
                  label="View added Test Cases"
                  onClick={() => handleToggleModes('view')}
                />
              </Box>
              <AddTestCaseInSuite
                selectedSuite={selectedSuite}
                selected={selected}
                setSelected={setSelected}
                technologyLayerOptions={technologyLayerOptions}
              />
            </>
          )}
        </Box>
      </Modal>

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleTestCaseRemove}
        title="Remove Test Case"
        body={`Are you sure you want to remove test case "${removeTestCase?.key}" from this test suite?`}
        buttonLabel="Remove"
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
      />
    </>
  );
};

export default ViewTestCaseModal;